
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { uploadBankCSV } from '../../../../modules/masterdata/actions';
import {
  selectUploadBankCSVLoading,
  selectUploadBankCSVSuccess,
  selectUploadBankCSVError,
} from '../../../../modules/masterdata/selectors';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
// @ts-ignore
import BanksCsv from '../../../../assets/Config/Banks.csv';

interface BankUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const BankUploadModal: React.FC<BankUploadModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectUploadBankCSVLoading);
  const success = useSelector(selectUploadBankCSVSuccess);
  const error = useSelector(selectUploadBankCSVError);

  const [file, setFile] = useState<File | null>(null);

  const handleClose = useCallback(() => {
    setFile(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (success && open) {
      handleClose();
    }
  }, [success, open, handleClose]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = () => {
    if (file) {
      dispatch(uploadBankCSV(file));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Bank CSV</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed #ccc',
            borderRadius: '8px',
            p: 4,
            mt: 2,
            cursor: 'pointer',
            bgcolor: '#fafafa',
            '&:hover': {
              bgcolor: '#f0f0f0',
            },
          }}
          component="label"
        >
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={handleFileChange}
          />
          <CloudUploadIcon sx={{ fontSize: 48, color: '#757575', mb: 1 }} />
          <Typography variant="body1" color="textSecondary">
            {file ? file.name : 'Click to upload CSV file'}
          </Typography>
        </Box>

        <Box sx={{ mt: 1, textAlign: 'right' }}>
          <Typography variant="body2">
            <a
              href={BanksCsv}
              download="Banks.csv"
              style={{ textDecoration: 'none', color: '#1976d2' }}
            >
              Download Template CSV
            </a>
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mt: 2 }} />}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!file || loading}
          color="primary"
        >
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankUploadModal;
